"use client";

/**
 * T8 embeddable widget snippet (docs/seo/authority-playbook.md).
 *
 * Copy-paste embed code shown on /free/* tool pages. The snippet pairs the
 * /embed/* iframe with a followed <a> back to the canonical tool page — the
 * anchor is the backlink (iframes pass no link equity), so it must stay in
 * the snippet. Keep the snippet free of hex colours (guardrail-no-hardcoded-hex
 * scans components/).
 */

import { useState } from "react";

interface EmbedWidgetSnippetProps {
  /** /embed route to iframe, e.g. "/embed/rams-generator" */
  embedPath: string;
  /** Canonical tool page the followed anchor points at, e.g. "/free/rams-generator" */
  toolPath: string;
  /** Anchor text for the followed backlink */
  linkText: string;
  /** iframe title attribute (accessibility) */
  iframeTitle: string;
}

const SITE = "https://thesitebook.co.uk";

export function EmbedWidgetSnippet({
  embedPath,
  toolPath,
  linkText,
  iframeTitle,
}: EmbedWidgetSnippetProps) {
  const [copied, setCopied] = useState(false);

  const snippet = [
    `<iframe src="${SITE}${embedPath}" title="${iframeTitle}" width="100%" height="380" style="border:0;max-width:520px" loading="lazy"></iframe>`,
    `<p><a href="${SITE}${toolPath}" target="_blank" rel="noopener">${linkText}</a></p>`,
  ].join("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions/http) — the user can still select
      // the visible snippet text manually.
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <pre className="overflow-x-auto rounded-lg bg-gray-50 p-4 font-mono text-xs leading-relaxed text-gray-700 whitespace-pre-wrap break-all">
        {snippet}
      </pre>
      <button
        type="button"
        onClick={copy}
        className="mt-4 inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
      >
        {copied ? "Copied!" : "Copy embed code"}
      </button>
      <p className="mt-3 text-xs text-gray-500">
        Keep the credit link under the widget — it tells your visitors where
        the tool lives.
      </p>
    </div>
  );
}
